import TextField from '@material-ui/core/TextField';
import {createStyles, MuiThemeProvider, withStyles} from '@material-ui/core/styles';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import InputMask from 'react-input-mask';
import {images} from '../../../../../assets/icons';
import {useStyles} from '../../../../hooks/styles';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';
import {useTheme} from '../../../../theme';
import {DeviceSize, useDeviceSize} from '../../../../theme/helpers';
import {Theme} from '../../../../theme/interface';
import {Text} from '../../../text';
import {BaseFieldProps, BaseFieldVariant, InputMaskTypes} from '../../index';
import {Eye} from '../passwordEye';
import {RegExps} from '../../../../constants/regexps';
import {newGuid} from '../../../../pipes/guid';
import {InputIconWrapper} from './iconWrapper';
import {useSelector} from 'react-redux';
import {themeSelector} from '../../../../../redux/selectors/runtime/theme';

export const BaseFieldText: React.FC<BaseFieldProps> = ({
  name,
  meta,
  type,
  autoFocus,
  label,
  placeholder,
  input,
  disabled,
  hint,
  mask,
  readOnly,
  left,
  leftStyle,
  right,
  rightStyle,
  style: customStyle = {},
  variant,
  rows,
  rowsMax,
  multiline,
  maxLength,
  disableErrorIcon,
  smallHeight,
}) => {
  const id = useRef<string>(newGuid());
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();
  const themeSettings = useSelector(themeSelector());
  const deviceSize = useDeviceSize();
  const isDesktop = useIsDesktop();
  const inputRef = useRef<HTMLInputElement>(null);
  const error: boolean = !!(meta?.touched && !!meta.error);
  const inputMask = type === 'phone' ? InputMaskTypes.tel : mask;

  const styles = useStyles(({theme: theme_, mediaOnly}) => {
    return {
      labelC: {
        ...mediaOnly(DeviceSize.desktop, theme_.fonts.h6Light, {...theme_.fonts.bodySmallLight}),
        color: theme_.color.labelText,
      },
      errorHelper: {
        color: theme_.html.textRed,
      },
      label: {
        ...theme_.helpText,
      },
    };
  });

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (error && meta?.error.isFirstField && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [error, meta?.error]);

  const {onChange, onBlur, onFocus, ...restInput} = input;
  const style = {width: deviceSize === DeviceSize.desktop ? 300 : undefined, ...customStyle};
  const isPassword = type === 'password';
  const htmlType = type === 'digits' || type === 'phone' ? 'text' : type;
  const type_ = isPassword ? (showPassword ? 'text' : 'password') : htmlType;

  const TextFieldComponent = useMemo(
    () => withStyles(createCustomStyles(theme, variant!, isDesktop, readOnly, smallHeight))(TextField),
    [variant, isDesktop, readOnly, themeSettings],
  );

  const onChangeHandler =
    type !== 'digits'
      ? onChange
      : (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          if (!input.onChange) {
            return;
          }
          const val = e.target.value.replace(/\D/g, '');
          const lengthRestriction = !maxLength || val.length <= maxLength;
          if ((val !== '' && !RegExps.digits.test(val)) || !lengthRestriction) {
            return;
          }
          input.onChange(val);
        };

  const InputProps: any = useMemo(
    () => ({
      startAdornment: left ? <InputIconWrapper style={leftStyle}>{left}</InputIconWrapper> : null,
      endAdornment:
        error && !disableErrorIcon ? (
          <InputIconWrapper>
            <images.Info height={20} width={20} color={theme.input.common.errorColor} />
          </InputIconWrapper>
        ) : isPassword ? (
          <InputIconWrapper>
            <Eye tag={name} onPress={() => setShowPassword(!showPassword)} showPassword={showPassword} />
          </InputIconWrapper>
        ) : right ? (
          <InputIconWrapper style={rightStyle}>{right}</InputIconWrapper>
        ) : null,
    }),
    [right, rightStyle, showPassword, error, theme, isPassword],
  );

  const inputProps: any = useMemo(
    () => ({
      'data-testid': input.name,
      ...(type === 'digits'
        ? {
            inputMode: 'numeric',
          }
        : {}),
      ...(maxLength
        ? {
            maxLength,
          }
        : {}),
    }),
    [type, maxLength, input.name],
  );

  const labelComponent = label
    ? ((
        <Text lineBreakMode='tail' numberOfLines={1} style={styles.labelC}>
          {label}
        </Text>
      ) as any)
    : null;

  const classes = useMemo(
    () => ({
      root: disableErrorIcon ? 'disable-error-icon' : undefined,
    }),
    [disableErrorIcon],
  );

  const helperText = useMemo(() => {
    const text = error ? meta?.error?.error : hint;
    return (
      <span data-testid={`${input.name}-help-text`} style={error ? {color: styles.errorHelper.color} : {}}>
        {text}
      </span>
    );
  }, [error, meta?.error?.error, hint, input.name, styles.errorHelper.color]);

  return (
    <MuiThemeProvider theme={theme.muiDefaultTheme}>
      {inputMask ? (
        <InputMask
          width={deviceSize === DeviceSize.desktop ? 300 : undefined}
          mask={inputMask}
          onChange={onChangeHandler}
          value={input.value ?? ''}
          readOnly={readOnly}
          onBlur={onBlur}
          disabled={disabled}
          onFocus={onFocus}>
          <TextFieldComponent
            placeholder={placeholder}
            error={error}
            style={style as any}
            type={type_}
            {...restInput}
            label={labelComponent}
            inputRef={inputRef}
            variant={variant}
            multiline={multiline}
            rows={rows}
            rowsMax={rowsMax}
            helperText={helperText}
            id={id.current}
            InputProps={InputProps}
            inputProps={inputProps}
            classes={classes}
          />
        </InputMask>
      ) : (
        <TextFieldComponent
          style={style as any}
          placeholder={placeholder}
          error={error}
          type={type_}
          {...input}
          value={input.value ?? ''}
          label={labelComponent}
          inputRef={inputRef}
          variant={variant}
          multiline={multiline}
          rows={rows}
          rowsMax={rowsMax}
          disabled={disabled || readOnly}
          helperText={helperText}
          onChange={(e) => {
            if (onChangeHandler) {
              onChangeHandler(e);
            }
          }}
          id={id.current}
          InputProps={InputProps}
          inputProps={inputProps}
          classes={classes}
        />
      )}
    </MuiThemeProvider>
  );
};

const createCustomStyles =
  (theme: Theme, variant: BaseFieldVariant, isDesktop: boolean, readOnly = false, smallHeight = false) =>
  () => {
    if (variant === 'outlined') {
      return createStyles({
        root: {
          '& .MuiOutlinedInput-input': {
            ...(isDesktop
              ? {
                  lineHeight: '28px',
                  padding: '8px 10px',
                  fontSize: theme.fonts.h5Light.fontSize,
                }
              : {
                  lineHeight: '20px',
                  padding: '8px 6px',
                  fontSize: theme.fonts.bodySmallLight.fontSize,
                }),
          },
          '& .MuiInputLabel-outlined:not(.Mui-focused):not(.MuiFormLabel-filled)': {
            transform: 'translate(14px, 11px) scale(1)',
          },
          '& .MuiOutlinedInput-root': {
            padding: smallHeight ? '0 6px' : '5px 6px',
            '& fieldset': {
              borderWidth: '1px !important',
              borderColor: theme.input.variants.outlined.borderColor,
            },
          },
          ...(readOnly
            ? {
                '& .Mui-disabled': {
                  color: theme.color.textColor,
                },
              }
            : {}),
        },
      });
    }

    return createStyles({
      root: {
        '& .MuiInputBase-input': {
          ...(isDesktop
            ? {fontSize: theme.fonts.h5Light.fontSize, fontWeight: theme.fonts.h5Light.fontWeight}
            : {fontSize: theme.fonts.body.fontSize, fontWeight: theme.fonts.body.fontWeight}),
        },
        '& .MuiInputLabel-root': {
          ...(isDesktop
            ? {fontSize: theme.fonts.h6Light.fontSize, fontWeight: theme.fonts.h6Light.fontWeight}
            : {fontSize: theme.fonts.bodySmallLight.fontSize, fontWeight: theme.fonts.bodySmallLight.fontWeight}),
        },
        '& .MuiInputLabel-formControl:not(.Mui-focused):not(.MuiFormLabel-filled)': {
          ...(isDesktop ? {transform: 'translate(0, 20px) scale(1)'} : {transform: 'translate(0, 14px) scale(1)'}),
        },
        '& .MuiInputBase-root': {
          color: theme.color.textColor,
        },
        '& .MuiInput-underline:before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
      },
    });
  };
